import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { fmt } from "@/lib/constants";
import { ArrowLeftRight, Info } from "lucide-react";

function amortize(balance, apr, monthly, promoApr = null, promoMonths = 0) {
  let bal = balance, interest = 0, m = 0;
  while (bal > 0.005 && m < 600) {
    m++;
    const rate = ((promoApr != null && m <= promoMonths) ? promoApr : apr) / 100 / 12;
    const i = bal * rate;
    interest += i; bal += i;
    if (monthly <= i && rate > 0) return { months: Infinity, interest: Infinity };
    bal -= Math.min(monthly, bal);
  }
  return { months: m, interest };
}

export default function BalanceTransferLab() {
  const [debts, setDebts] = useState([]);
  const [id, setId] = useState("");
  const [promoApr, setPromoApr] = useState(0);
  const [promoMonths, setPromoMonths] = useState(15);
  const [fee, setFee] = useState(3);
  const [monthly, setMonthly] = useState("");

  useEffect(() => {
    api.get("/debts").then(r => {
      const list = r.data.filter(d => d.balance > 0);
      setDebts(list);
      if (list.length) { setId(list[0].id); setMonthly(String(list[0].minimum_payment)); }
    }).catch(() => {});
  }, []);

  const debt = debts.find(d => d.id === id);
  useEffect(() => { if (debt) setMonthly(String(debt.minimum_payment)); }, [id]); // eslint-disable-line

  const result = useMemo(() => {
    if (!debt) return null;
    const pay = parseFloat(monthly) || 0;
    if (pay <= 0) return null;
    const keep = amortize(debt.balance, debt.interest_rate, pay);
    const feeAmt = debt.balance * (parseFloat(fee) || 0) / 100;
    const tr = amortize(debt.balance + feeAmt, debt.interest_rate, pay, parseFloat(promoApr) || 0, parseInt(promoMonths) || 0);
    const keepCost = keep.interest;
    const transferCost = tr.interest + feeAmt;
    const saved = keepCost - transferCost;
    return { keep, tr, feeAmt, keepCost, transferCost, saved };
  }, [debt, monthly, fee, promoApr, promoMonths]);

  if (!debts.length) return null;

  return (
    <div className="p-5 rounded-2xl border border-border bg-card" data-testid="balance-transfer-lab">
      <div className="flex items-center gap-2">
        <ArrowLeftRight className="h-5 w-5 text-violet-500" />
        <div className="font-display font-bold text-lg">Balance transfer what-if</div>
      </div>
      <p className="text-sm text-muted-foreground mt-1">Move a high-APR balance to a 0%/promo card and see whether the transfer fee is worth it.</p>

      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Field label="Balance to move" full>
          <select value={id} onChange={e => setId(e.target.value)} data-testid="bt-debt-select" className={inp}>
            {debts.map(d => <option key={d.id} value={d.id}>{d.name} · {fmt(d.balance)} @ {d.interest_rate}%</option>)}
          </select>
        </Field>
        <Field label="Promo APR %"><input type="number" min="0" step="0.1" value={promoApr} onChange={e => setPromoApr(e.target.value)} data-testid="bt-promo-apr" className={inp} /></Field>
        <Field label="Promo months"><input type="number" min="1" value={promoMonths} onChange={e => setPromoMonths(e.target.value)} data-testid="bt-promo-months" className={inp} /></Field>
        <Field label="Transfer fee %"><input type="number" min="0" step="0.1" value={fee} onChange={e => setFee(e.target.value)} data-testid="bt-fee" className={inp} /></Field>
        <Field label="Monthly payment"><input type="number" min="0" step="1" value={monthly} onChange={e => setMonthly(e.target.value)} data-testid="bt-monthly" className={inp} /></Field>
      </div>

      {result && (
        <div className="mt-4" data-testid="bt-result">
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-xl bg-accent/40 border border-border/60">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Keep it ({debt.interest_rate}%)</div>
              <div className="font-mono font-bold text-lg mt-1 text-rose-500">{result.keep.interest === Infinity ? "Never" : fmt(result.keepCost)}</div>
              <div className="text-[10px] text-muted-foreground">interest{result.keep.months !== Infinity ? ` · ${result.keep.months} mo` : ""}</div>
            </div>
            <div className="p-3 rounded-xl bg-accent/40 border border-border/60">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Transfer (+{fmt(result.feeAmt)} fee)</div>
              <div className="font-mono font-bold text-lg mt-1 text-violet-500">{result.tr.interest === Infinity ? "Never" : fmt(result.transferCost)}</div>
              <div className="text-[10px] text-muted-foreground">interest + fee{result.tr.months !== Infinity ? ` · ${result.tr.months} mo` : ""}</div>
            </div>
          </div>
          <div className={`mt-3 p-3 rounded-xl text-sm font-semibold ${result.saved > 0 ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/25" : "bg-rose-500/10 text-rose-500 border border-rose-500/25"}`} data-testid="bt-verdict">
            {result.saved > 0
              ? `Transferring could save you ${fmt(result.saved)} — worth it if you can clear most of it during the promo window.`
              : `Not worth it — the ${fmt(result.feeAmt)} fee outweighs the interest saved. Keep the balance where it is.`}
          </div>
          <div className="mt-2 flex items-start gap-1.5 text-[11px] text-muted-foreground"><Info className="h-3 w-3 mt-0.5 shrink-0" /> Estimate at the same monthly payment. After the promo ends, the leftover balance accrues at the original APR.</div>
        </div>
      )}
    </div>
  );
}

const inp = "w-full px-2.5 py-2 rounded-lg bg-background border border-border text-sm font-mono focus:outline-none focus:ring-2 focus:ring-violet-500/40 mt-1";
function Field({ label, children, full }) {
  return <label className={`block ${full ? "col-span-2 sm:col-span-1" : ""}`}><span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</span>{children}</label>;
}
